/**
 * HR Service Hooks
 * React hooks for accessing HR management services
 */
import { useCCMS } from '../useCCMS';
/**
 * Hook to access all HR-related services
 */
export function useHrServices() {
    const { services } = useCCMS();
    if (!services?.hr) {
        throw new Error('HR services not available. Ensure CCMSProvider is configured with HR services.');
    }
    return services.hr;
}
/**
 * Hook to access DutyService
 */
export function useDutyService() {
    return useHrServices().duty;
}
/**
 * Hook to access EmployeeDutyService
 */
export function useEmployeeDutyService() {
    return useHrServices().employeeDuty;
}
/**
 * Hook to access EmployeeFileCategoryService
 */
export function useEmployeeFileCategoryService() {
    return useHrServices().employeeFileCategory;
}
/**
 * Hook to access EmployeeFileTypeService
 */
export function useEmployeeFileTypeService() {
    return useHrServices().employeeFileType;
}
/**
 * Hook to access EmployeeFileService
 */
export function useEmployeeFileService() {
    return useHrServices().employeeFile;
}
/**
 * Hook to access EmployeeStatusService
 */
export function useEmployeeStatusService() {
    return useHrServices().employeeStatus;
}
/**
 * Hook to access EmployeeTypeService
 */
export function useEmployeeTypeService() {
    return useHrServices().employeeType;
}
/**
 * Hook to access EmployeeService
 */
export function useEmployeeService() {
    return useHrServices().employee;
}
/**
 * Hook to access LeaveDocumentTypeService
 */
export function useLeaveDocumentTypeService() {
    return useHrServices().leaveDocumentType;
}
/**
 * Hook to access LeaveDocumentService
 */
export function useLeaveDocumentService() {
    return useHrServices().leaveDocument;
}
/**
 * Hook to access LeaveDutyService
 */
export function useLeaveDutyService() {
    return useHrServices().leaveDuty;
}
/**
 * Hook to access LeaveNotificationQueueService
 */
export function useLeaveNotificationQueueService() {
    return useHrServices().leaveNotificationQueue;
}
/**
 * Hook to access LeaveStatusService
 */
export function useLeaveStatusService() {
    return useHrServices().leaveStatus;
}
/**
 * Hook to access LeaveTypeService
 */
export function useLeaveTypeService() {
    return useHrServices().leaveType;
}
/**
 * Hook to access LeaveService
 */
export function useLeaveService() {
    return useHrServices().leave;
}
/**
 * Hook to access LeaveApprovalService: line approver inbox, HR final approval queue,
 * approve / reject / ask the employee (api/leave-approvals).
 */
export function useLeaveApprovalService() {
    return useHrServices().leaveApproval;
}
/**
 * Hook to access LeaveApprovalRuleService: line approver rules, chain preview, health and
 * lookups (api/leave-approval-rules, HRAdmin).
 */
export function useLeaveApprovalRuleService() {
    return useHrServices().leaveApprovalRule;
}
/**
 * Hook to access PolicyService
 */
export function usePolicyService() {
    return useHrServices().policy;
}
/**
 * Hook to access PolicyCategoryService
 */
export function usePolicyCategoryService() {
    return useHrServices().policyCategory;
}
/**
 * Hook to access PolicySignatureService
 */
export function usePolicySignatureService() {
    return useHrServices().policySignature;
}
/**
 * Hook to access PolicyAssignmentsService — admin-side aggregate listing
 * of policy assignments (used by HR Operations badge subscriber etc.).
 */
export function usePolicyAssignmentsService() {
    return useHrServices().policyAssignments;
}
/** HRAnnouncementService — admin CRUD for HR announcements. */
export function useHRAnnouncementService() {
    return useHrServices().hrAnnouncement;
}
/** HRAnnouncementTypeService — lookup of announcement types. */
export function useHRAnnouncementTypeService() {
    return useHrServices().hrAnnouncementType;
}
/** HRAnnouncementStatusService — lookup of announcement statuses. */
export function useHRAnnouncementStatusService() {
    return useHrServices().hrAnnouncementStatus;
}
/** HRSettingsService — module-wide HR + PaySpace settings. */
export function useHRSettingsService() {
    return useHrServices().hrSettings;
}
/** HRNotificationSettingsService — per-NotificationType channel toggles. */
export function useHRNotificationSettingsService() {
    return useHrServices().hrNotificationSettings;
}
/** PaySpaceSyncService — admin-only sync triggers + per-user balance lookups. */
export function usePaySpaceSyncService() {
    return useHrServices().paySpaceSync;
}
//# sourceMappingURL=useHrServices.js.map